import React, { useState, useEffect, useRef } from "react";
import {
  SearchOutlined,
  UploadOutlined,
  ExclamationCircleOutlined,
  CloudSyncOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  Card,
  Input,
  Form,
  Button,
  Tabs,
  message,
  Upload,
  Tag,
  Row,
  Col,
  Select,
  Modal,
  Table,
} from "antd";
import "./adminPanel.css";
import AdminTable from "./AdminTable";
import axios from "axios";
import Highlighter from "react-highlight-words";
import { customFetch } from "../customFetch";

const { TabPane } = Tabs;
const { Option } = Select;

const PY_API_URL = process.env.REACT_APP_PY_API_URL;

const AdminPanel = () => {
  const [messageApi, contextHolder] = message.useMessage();
  message.config({
    duration: 6,
  });
  const success = () => {
    messageApi.open({
      type: "success",
      content: "Submitted",
    });
  };
  // State hooks for all inputs
  const [projectNumber, setProjectNumber] = useState("");
  const [program, setProgram] = useState("");
  const [pm, setPm] = useState("");
  const [customer, setCustomer] = useState("");
  const [project, setProject] = useState("");
  const [cdc, setCdc] = useState("");
  const [division, setDivision] = useState("");
  const [testType, setTestType] = useState("");
  const [group, setGroup] = useState("");
  const [costCenterProjectNumber, setCostCenterProjectNumber] = useState("");
  const [costCenterProgram, setCostCenterProgram] = useState("");
  const [costCenterPm, setCostCenterPm] = useState("");
  const [bckPm, setBckPm] = useState("");
  const [costCenterCdc, setCostCenterCdc] = useState("");
  const [chargeTo, setChargeTo] = useState("");
  const [fixtureName, setFixtureName] = useState("");
  const [climaticChamberName, setClimaticChamberName] = useState("");
  const [testName, setTestName] = useState("");
  const [testNumber, setTestNumber] = useState("");
  const [file, setFile] = useState(null);

  //////////////////////////// ETO PROJECT ////////////////////////////////////

  //modifying
  const [etoProjects, setEtoProjects] = useState({});
  const [selectedProject, setSelectedProject] = useState(null);
  const [modifiedProject, setModifiedProject] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = () => {
    customFetch(`${PY_API_URL}/api/get_projects_admin`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        console.log("Full API Response:", data);

        const filteredProjects = Object.keys(data)
          .filter((key) => data[key].status === "Active")
          .reduce((obj, key) => {
            obj[key] = data[key];
            return obj;
          }, {});

        setEtoProjects(filteredProjects);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  };

  const handleModifyProject = async () => {
    if (!selectedProject) {
      message.error("Please select a project to modify.");
      return;
    }

    try {
      const response = await axios.post(`${PY_API_URL}/api/modify_project`, {
        current_project_number: selectedProject,
        new_project_number: projectNumber,
        program: program,
        pm: pm,
        customer: customer,
        project: project,
        cdc: cdc,
        division: division,
      });

      if (response.status === 200 && response.data.success) {
        message.success(`Project ${selectedProject} successfully modified.`);
        fetchProjects();
        setSelectedProject(null);
        setModifiedProject("");
      } else {
        message.error(response.data.error || "Failed to modify the project.");
      }
    } catch (error) {
      console.error("Error modifying project:", error);
      message.error("An error occurred while modifying the project.");
    }
  };

  const ProjectColumns = [
    {
      title: "Project Number",
      dataIndex: "projectNumber",
      key: "projectNumber",
    },
    { title: "Program", dataIndex: "program", key: "program" },
    { title: "PM", dataIndex: "pm", key: "pm" },
    { title: "Customer", dataIndex: "customer", key: "customer" },
    { title: "Project", dataIndex: "project", key: "project" },
    { title: "CDC", dataIndex: "cdc", key: "cdc" },
    { title: "Division", dataIndex: "division", key: "division" },
    { title: "Status", dataIndex: "status", key: "status" },
  ];

  //////////////////////////// JOB TYPE ////////////////////////////////////

  const [jobTypes, setJobTypes] = useState({});
  const [selectedJobType, setSelectedJobType] = useState(null);

  useEffect(() => {
    fetchJobTypes();
  }, []);

  const fetchJobTypes = () => {
    customFetch(`${PY_API_URL}/api/get_job_types_admin`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        setJobTypes(data);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  };

  const handleModifyJobType = async () => {
    if (!selectedJobType) {
      message.error("Please select a job type to modify.");
      return;
    }

    try {
      const response = await axios.post(`${PY_API_URL}/api/modify_job_type`, {
        current_test_type: selectedJobType,
        new_test_type: testType,
        new_group: group,
      });

      if (response.status === 200 && response.data.success) {
        message.success(`Job Type ${selectedJobType} successfully modified.`);

        fetchJobTypes();

        setSelectedJobType(null);
        setTestType("");
        setGroup("");
      } else {
        message.error(response.data.error || "Failed to modify the job type.");
      }
    } catch (error) {
      console.error("Error modifying job type:", error);
      message.error("An error occurred while modifying the job type.");
    }
  };

  const JobTypeColumns = [
    {
      title: "Job Type",
      dataIndex: "test_type",
      key: "test_type",
    },
    {
      title: "Group",
      dataIndex: "group",
      key: "group",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
    },
  ];

  //////////////////////////// COST CENTER DETAILS ////////////////////////////////////

  const [selectedCostCenter, setSelectedCostCenter] = useState(null);
  const [costCenters, setCostCenters] = useState([]);

  const fetchCostCenters = () => {
    customFetch(`${PY_API_URL}/api/get_cost_centers_admin`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        console.log("Full API Response:", data);

        if (!Array.isArray(data)) {
          console.error("Invalid data format:", data);
          message.error("Failed to fetch cost centers: Invalid data format.");
          return;
        }

        // Deduplicate
        const uniqueCenters = [
          ...new Map(
            data.map((c) => [c.costCenter + c.costCenterDescription, c])
          ).values(),
        ];

        setCostCenters(uniqueCenters);
      })
      .catch((error) => {
        console.error("Error fetching cost centers:", error);
        message.error("Error fetching cost centers");
      });
  };

  useEffect(() => {
    fetchCostCenters();
  }, []);

  const handleModifyCostCenter = async () => {
    if (!selectedCostCenter) {
      message.error("Please select a cost center to modify.");
      return;
    }

    try {
      const response = await axios.post(
        `${PY_API_URL}/api/modify_cost_center`,
        {
          current_project_number: selectedCostCenter,
          new_project_number: costCenterProjectNumber,
          program: costCenterProgram,
          pm: costCenterPm,
          bckPm: bckPm,
          cdc: costCenterCdc,
          chargeTo: chargeTo,
        }
      );

      if (response.status === 200 && response.data.success) {
        message.success(
          `Cost Center ${selectedCostCenter} successfully modified.`
        );

        fetchCostCenters();

        setSelectedCostCenter(null);
        setCostCenterProjectNumber("");
        setCostCenterProgram("");
        setCostCenterPm("");
        setBckPm("");
        setCostCenterCdc("");
        setChargeTo("");
      } else {
        message.error(
          response.data.error || "Failed to modify the cost center."
        );
      }
    } catch (error) {
      console.error("Error modifying cost center:", error);
      message.error("An error occurred while modifying the cost center.");
    }
  };

  const CostCenterColumns = [
    { title: "Cost Center", dataIndex: "costCenter", key: "costCenter" },
    {
      title: "Description",
      dataIndex: "costCenterDescription",
      key: "costCenterDescription",
    },
    {
      title: "Manager",
      dataIndex: "costCenterManager",
      key: "costCenterManager",
    },
    {
      title: "Approver",
      dataIndex: "costCenterApprover",
      key: "costCenterApprover",
    },
    { title: "CDC", dataIndex: "cdc", key: "cdc" },
    { title: "Charge To", dataIndex: "chargeTo", key: "chargeTo" },
    { title: "Status", dataIndex: "status", key: "status" },
  ];

  //////////////////////////// FIXTURE ////////////////////////////////////

  const [fixtures, setFixtures] = useState([]);
  const [selectedFixture, setSelectedFixture] = useState(null);
  console.log(selectedFixture);
  const [modifiedFixtureName, setModifiedFixtureName] = useState("");

  useEffect(() => {
    fetchFixtures();
  }, []);

  const fetchFixtures = () => {
    customFetch(`${PY_API_URL}/api/get_fixtures_admin`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        setFixtures(data);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  };

  const handleModifyFixture = async () => {
    if (!selectedFixture || !modifiedFixtureName) {
      message.error("Both the current and new fixture names are required.");
      return;
    }

    try {
      const response = await axios.post(`${PY_API_URL}/api/modify_fixture`, {
        current_fixture_name: selectedFixture,
        new_fixture_name: modifiedFixtureName,
      });

      if (response.status === 200 && response.data.success) {
        message.success(
          `Fixture "${selectedFixture}" successfully modified to "${modifiedFixtureName}"`,
          5
        );

        fetchFixtures();
        setSelectedFixture(null);
        setModifiedFixtureName("");
      } else {
        message.error(response.data.error || "Failed to modify the fixture.");
      }
    } catch (error) {
      console.error("Error modifying fixture:", error);
      message.error("An error occurred while modifying the fixture.");
    }
  };

  const FixtureColumns = [
    { title: "Fixture Name", dataIndex: "name", key: "name" },
    {
      title: "Calibration Expiration",
      dataIndex: "calibrationExpirationDate",
      key: "calibrationExpirationDate",
    },
    { title: "Status", dataIndex: "status", key: "status" },
  ];

  //////////////////////////// CLIMATIC CHAMBER ////////////////////////////////////

  const [climaticChambers, setClimaticChambers] = useState([]);
  const [selectedChamber, setSelectedChamber] = useState(null);
  const [modifiedChamberName, setModifiedChamberName] = useState("");

  const fetchClimaticChambers = () => {
    customFetch(`${PY_API_URL}/api/get_climatic_chambers_admin`, {
      method: "GET",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        setClimaticChambers(data);
      })
      .catch((error) => {
        console.error("Error fetching climatic chambers:", error);
      });
  };

  useEffect(() => {
    fetchClimaticChambers();
  }, []);

  const handleModifyChamber = async () => {
    if (!selectedChamber || !modifiedChamberName) {
      message.error(
        "Both the current and new climatic chamber names are required."
      );
      return;
    }

    try {
      const response = await axios.post(
        `${PY_API_URL}/api/modify_climatic_chamber`,
        {
          current_chamber_name: selectedChamber,
          new_chamber_name: modifiedChamberName,
        }
      );

      if (response.status === 200 && response.data.success) {
        message.success(
          `Climatic Chamber "${selectedChamber}" successfully modified to "${modifiedChamberName}"`,
          5
        );

        fetchClimaticChambers();
        setSelectedChamber(null);
        setModifiedChamberName("");
      } else {
        message.error(
          response.data.error || "Failed to modify the climatic chamber."
        );
      }
    } catch (error) {
      console.error("Error modifying climatic chamber:", error);
      message.error("An error occurred while modifying the climatic chamber.");
    }
  };

  const ClimaticChamberColumns = [
    { title: "Chamber Name", dataIndex: "name", key: "name" },
    {
      title: "Calibration Expiration",
      dataIndex: "calibrationExpiringDate",
      key: "calibrationExpiringDate",
    },
    { title: "Status", dataIndex: "status", key: "status" },
  ];

  const handleProjectNumberChange = (e) => setProjectNumber(e.target.value);
  const handleProgramChange = (e) => setProgram(e.target.value);
  const handlePmChange = (e) => setPm(e.target.value);
  const handleCustomerChange = (e) => setCustomer(e.target.value);
  const handleProjectChange = (e) => setProject(e.target.value);
  const handleCdcChange = (e) => setCdc(e.target.value);
  const handleDivisionChange = (e) => setDivision(e.target.value);
  const handleTestTypeChange = (e) => setTestType(e.target.value);
  const handleGroupChange = (e) => setGroup(e.target.value);
  const handleCostCenterProjectNumberChange = (e) =>
    setCostCenterProjectNumber(e.target.value);
  const handleCostCenterProgramChange = (e) =>
    setCostCenterProgram(e.target.value);
  const handleCostCenterPmChange = (e) => setCostCenterPm(e.target.value);
  const handleBckPmChange = (e) => setBckPm(e.target.value);
  const handleCostCenterCdcChange = (e) => setCostCenterCdc(e.target.value);
  const handleChargeToChange = (e) => setChargeTo(e.target.value);
  const handleFixtureNameChange = (e) => setFixtureName(e.target.value);
  const handleClimaticChamberNameChange = (e) =>
    setClimaticChamberName(e.target.value);
  const handleTestNameChange = (e) => setTestName(e.target.value);
  const handleTestNumberChange = (e) => setTestNumber(e.target.value);

  // Handle button clicks
  const handleSubmitProject = () => {
    try {
      const response = axios.post(`${PY_API_URL}/api/add_new_eto_project`, {
        projectNumber: projectNumber,
        program: program,
        pm: pm,
        customer: customer,
        project: project,
        cdc: cdc,
        division: division,
      });
      // success()
      message.success("Added successfully");
    } catch (error) {
      console.error("Error submitting new eto project:", error);
    }
  };

  const handleSubmitJobType = () => {
    try {
      const response = axios.post(`${PY_API_URL}/api/add_new_job_type`, {
        testType: testType,
        group: group,
      });
      // success();
      message.success("Added successfully");
    } catch (error) {
      console.error("Error submitting new job type:", error);
    }
  };

  const handleSubmitCostCenter = () => {
    try {
      const response = axios.post(`${PY_API_URL}/api/add_cost_center`, {
        projectNumber: costCenterProjectNumber,
        program: costCenterProgram,
        pm: costCenterPm,
        bckpm: bckPm,
        cdc: costCenterCdc,
        chargeTo: chargeTo,
      });
      // success();
      message.success("Added successfully");
    } catch (error) {
      console.error("Error submitting new cost center:", error);
    }
  };

  const handleSubmitFixture = () => {
    try {
      const response = axios.post(`${PY_API_URL}/api/add_new_fixture`, {
        fixtureName: fixtureName,
      });
      // success()
      message.success("Added successfully");
    } catch (error) {
      console.error("Error submitting new fixture:", error);
    }
  };

  const handleSubmitClimaticChamber = () => {
    try {
      const response = axios.post(`${PY_API_URL}/api/add_climatic_chamber`, {
        chamberName: climaticChamberName,
      });
      // success();
      message.success("Added successfully");
    } catch (error) {
      console.error("Error submitting new climatic chamber:", error);
    }
  };

  const [comparisonData, setComparisonData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [oldComparisonData, setOldComparisonData] = useState([]);
  const [oldSyncLoading, setOldSyncLoading] = useState(false);
  const [isOldModalVisible, setIsOldModalVisible] = useState(false);
  const [loadingRow, setLoadingRow] = useState(null);
  const [oldLoading, setOldLoading] = useState(false);

  const showSyncConfirm = () => {
    setIsModalVisible(true);
  };

  const handleSyncCancel = () => {
    setIsModalVisible(false); // Close modal when user cancels
  };
  const showOldSyncConfirm = () => setIsOldModalVisible(true);
  const handleOldSyncCancel = () => setIsOldModalVisible(false);

  // Fetch the comparison data between the DB and the network folder
  const fetchTestInstructionsComparison = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${PY_API_URL}/api/test-instructions-status`
      );
      setComparisonData(response.data.comparison);
    } catch (error) {
      if (error.response && error.response.status === 429) {
        message.error("Too many requests! Please wait a moment.");
      } else if (error.response?.data?.error) {
        message.error(`Error: ${error.response.data.error}`);
      } else {
        message.error("An unknown error occurred.");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchOldTestInstructionsComparison = async () => {
    setOldLoading(true);
    try {
      const response = await axios.get(
        `${PY_API_URL}/api/get_old_test_instructions_comparison`
      );
      setOldComparisonData(response.data.comparison);
      console.log("old ti data: ", oldComparisonData);
    } catch (error) {
      console.error("Error fetching old test instructions:", error);
    } finally {
      setOldLoading(false);
    }
  };

  // Add individual test instruction to the database
  const handleAddMissingTestInstruction = async (testNumber, isCurrent) => {
    setLoadingRow(testNumber);
    try {
      const response = await axios.post(
        `${PY_API_URL}/api/add_missing_test_instruction`,
        { testNumber, isCurrent } // Now sending isCurrent to backend
      );
      message.success(`Test instruction ${testNumber} added successfully!`);
      // Decide which fetch function to call based on isCurrent
      if (isCurrent === 1) {
        fetchTestInstructionsComparison();
      } else {
        fetchOldTestInstructionsComparison();
      }
    } catch (error) {
      if (error.response && error.response.status === 429) {
        message.error(
          "Rate limit exceeded. Please wait a moment and try again."
        );
      } else {
        message.error("Error adding test instruction. Please try again.");
      }
      console.error("Error:", error);
    } finally {
      setLoadingRow(null);
    }
  };

  const handleSyncConfirm = async () => {
    setSyncLoading(true);
    setIsModalVisible(false); // Close modal when confirmed

    try {
      await handleSynchronize();
    } catch (error) {
      console.error("Error during synchronization:", error);
    } finally {
      setSyncLoading(false);
    }
  };

  const handleOldSyncConfirm = async () => {
    setOldSyncLoading(true);
    try {
      const response = await axios.post(
        `${PY_API_URL}/api/sync_old_test_instructions`
      );
      message.success(
        `Synchronized ${response.data.success.length} old test instructions.`
      );
      if (response.data.errors.length > 0) {
        message.warning(
          `${response.data.errors.length} items could not be synchronized.`
        );
        console.log("Old Sync Errors:", response.data.errors);
      }
      fetchOldTestInstructionsComparison(); // Refresh table
    } catch (error) {
      message.error("Failed to synchronize old test instructions.");
      console.error("Error:", error);
    } finally {
      setOldSyncLoading(false);
      setIsOldModalVisible(false);
    }
  };

  // Synchronize all missing test instructions
  const handleSynchronize = async () => {
    setSyncLoading(true);
    try {
      const response = await axios.post(
        `${PY_API_URL}/api/synchronize_test_instructions`
      );
      message.success(
        `Synchronized ${response.data.success.length} test instructions.`
      );
      if (response.data.errors.length > 0) {
        message.warning(
          `${response.data.errors.length} items could not be synchronized.`
        );
        console.log("Sync Errors:", response.data.errors);
      }
      fetchTestInstructionsComparison();
    } catch (error) {
      message.error("Error synchronizing test instructions.");
      console.error("Error:", error);
    } finally {
      setSyncLoading(false);
    }
  };

  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);

  // Search handling function
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search Test Number`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Search
        </Button>
        <Button
          onClick={() => handleReset(clearFilters)}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  // Handlers for search and reset
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const TIcolumns = [
    {
      title: "Test Number",
      dataIndex: "testNumber",
      key: "testNumber",
      ...getColumnSearchProps("testNumber"),
    },
    {
      title: "Status",
      key: "status",
      filters: [
        { text: "Missing in DB", value: "missingInDB" },
        { text: "Missing in Network Folder", value: "missingInFolder" },
        { text: "Synchronized", value: "synchronized" },
      ],
      onFilter: (value, record) => {
        if (value === "missingInDB") return record.missingInDB;
        if (value === "missingInFolder") return record.missingInFolder;
        if (value === "synchronized")
          return !record.missingInDB && !record.missingInFolder;
        return true;
      },
      render: (_, record) => {
        if (record.missingInDB) {
          return <Tag color="red">Missing in DB</Tag>;
        } else if (record.missingInFolder) {
          return <Tag color="orange">Missing in Network Folder</Tag>;
        }
        return <Tag color="green">Synchronized</Tag>;
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <>
          {record.missingInDB && (
            <Button
              size="medium"
              type="primary"
              loading={loadingRow === record.testNumber}
              disabled={loadingRow === record.testNumber}
              onClick={() =>
                handleAddMissingTestInstruction(
                  record.testNumber,
                  record.isCurrent
                )
              }
            >
              Add to DB
            </Button>
          )}
        </>
      ),
    },
  ];

  // const [comparisonData, setComparisonData] = useState([]);
  // const [loading, setLoading] = useState(false); // Track loading state

  // const fetchTestInstructionsComparison = async () => {
  //   setLoading(true);
  //   try {
  //     const response = await axios.get(
  //       `${PY_API_URL}/api/test-instructions-status`
  //     );
  //     setComparisonData(response.data.comparison);
  //   } catch (error) {
  //     if (error.response && error.response.status === 429) {
  //       message.error("Too many requests! Please wait a moment.");
  //     } else if (error.response?.data?.error) {
  //       message.error(`Error: ${error.response.data.error}`);
  //     } else {
  //       message.error("An unknown error occurred.");
  //     }
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleSubmitTestInstruction = async () => {
    if (!file || !testName || !testNumber) {
      message.error("Please complete all fields and upload a file.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("testName", testName);
    formData.append("testNumber", testNumber);

    try {
      const response = await axios.post(
        `${PY_API_URL}/api/add_new_test_instruction`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      message.success("Test instruction added successfully");
      fetchTestInstructionsComparison();
    } catch (error) {
      message.error("Error submitting new test instruction");
      console.error("Error submitting new test instruction:", error);
    }
  };

  const handleUpload = (file) => {
    if (file.type !== "application/pdf") {
      message.error(`${file.name} is not a PDF file.`);
      return Upload.LIST_IGNORE;
    }
    setFile(file);
    return false; // Prevent automatic upload
  };

  // const handleAddMissingTestInstruction = async (testNumber) => {
  //   try {
  //     const response = await axios.post(
  //       `${PY_API_URL}/api/add_missing_test_instruction`,
  //       { testNumber }
  //     );
  //     message.success(`Test instruction ${testNumber} added successfully!`);

  //     // Refresh table after adding
  //     fetchTestInstructionsComparison();
  //   } catch (error) {
  //     message.error("Error adding missing test instruction.");
  //     console.error("Error:", error);
  //   }
  // };

  // const TIcolumns = [
  //   {
  //     title: "Test Number",
  //     dataIndex: "testNumber",
  //     key: "testNumber",
  //   },
  //   {
  //     title: "Exists in Network Folder",
  //     dataIndex: "existsInFolder",
  //     key: "existsInFolder",
  //     filters: [
  //       { text: "Yes", value: true },
  //       { text: "No", value: false },
  //     ],
  //     onFilter: (value, record) => record.existsInFolder === value,
  //     render: (exists) =>
  //       exists ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>,
  //   },
  //   {
  //     title: "Action",
  //     key: "action",
  //     render: (_, record) =>
  //       !record.existsInFolder ? (
  //         <Button
  //           size="small"
  //           type="primary"
  //           onClick={() => handleAddMissingTestInstruction(record.testNumber)}
  //         >
  //           Add to DB
  //         </Button>
  //       ) : null, // No button if the test already exists
  //   },
  // ];

  return (
    <Card className="admin-panel-container">
      <h2
        style={{
          display: "inline-block",
          marginRight: "10px",
          marginBottom: "20px",
          marginTop: "0px",
        }}
      >
        Admin Panel
      </h2>
      <Tabs defaultActiveKey="1" tabPosition="left">
        <TabPane tab="ETO Project" key="1">
          <Tabs centered>
            <TabPane tab="Add" key="1.1">
              <Card className="form-card" bordered>
                <Form
                  layout="horizontal"
                  labelCol={{ span: 6 }}
                  wrapperCol={{ span: 16 }}
                  style={{ maxWidth: 600 }}
                >
                  <Form.Item label="Project Number">
                    <Input
                      value={projectNumber}
                      onChange={handleProjectNumberChange}
                      placeholder="T70330 Carryover WR MAP"
                    />
                  </Form.Item>
                  <Form.Item label="Program">
                    <Input
                      value={program}
                      onChange={handleProgramChange}
                      placeholder="Carryover WR MAP"
                    />
                  </Form.Item>
                  <Form.Item label="PM">
                    <Input
                      value={pm}
                      onChange={handlePmChange}
                      placeholder="Paolo Casalini"
                    />
                  </Form.Item>
                  <Form.Item label="Customer">
                    <Input
                      value={customer}
                      onChange={handleCustomerChange}
                      placeholder="Generic"
                    />
                  </Form.Item>
                  <Form.Item label="Project">
                    <Input
                      value={project}
                      onChange={handleProjectChange}
                      placeholder="T70330"
                    />
                  </Form.Item>
                  <Form.Item label="CDC">
                    <Input value={cdc} onChange={handleCdcChange} />
                  </Form.Item>
                  <Form.Item label="Division">
                    <Input
                      value={division}
                      onChange={handleDivisionChange}
                      placeholder="MAP (295)"
                    />
                  </Form.Item>
                  <Form.Item className="form-item">
                    <Button
                      className="form-btn"
                      type="primary"
                      onClick={handleSubmitProject}
                      disabled={
                        !projectNumber.trim() ||
                        !program.trim() ||
                        !pm.trim() ||
                        !customer.trim() ||
                        !project.trim() ||
                        !division.trim()
                      }
                    >
                      Submit New ETO Project
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            </TabPane>
            <TabPane tab="Modify" key="1.2">
              <Card className="form-card" bordered>
                <Form
                  layout="horizontal"
                  labelCol={{ span: 6 }}
                  wrapperCol={{ span: 16 }}
                  style={{ maxWidth: 600 }}
                >
                  <Form.Item label="Select Project">
                    <Select
                      showSearch
                      placeholder="Select a project to modify"
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option?.children
                          ?.toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      onChange={(value) => {
                        setSelectedProject(value);
                        const selected = etoProjects[value];
                        if (selected) {
                          setProjectNumber(value);
                          setProgram(selected.program || "");
                          setPm(selected.pm || "");
                          setCustomer(selected.customer || "");
                          setProject(selected.project || "");
                          setCdc(selected.cdc || "");
                          setDivision(selected.division || "");
                        }
                      }}
                    >
                      {Object.keys(etoProjects).map((project) => (
                        <Option key={project} value={project}>
                          {project}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  {selectedProject && (
                    <>
                      <Form.Item label="Project Number">
                        <Input
                          value={projectNumber}
                          onChange={handleProjectNumberChange}
                        />
                      </Form.Item>
                      <Form.Item label="Program">
                        <Input value={program} onChange={handleProgramChange} />
                      </Form.Item>
                      <Form.Item label="PM">
                        <Input value={pm} onChange={handlePmChange} />
                      </Form.Item>
                      <Form.Item label="Customer">
                        <Input
                          value={customer}
                          onChange={handleCustomerChange}
                        />
                      </Form.Item>
                      <Form.Item label="Project">
                        <Input value={project} onChange={handleProjectChange} />
                      </Form.Item>
                      <Form.Item label="CDC">
                        <Input value={cdc} onChange={handleCdcChange} />
                      </Form.Item>
                      <Form.Item label="Division">
                        <Input
                          value={division}
                          onChange={handleDivisionChange}
                        />
                      </Form.Item>
                      <Form.Item className="form-item">
                        <Button
                          className="form-btn"
                          type="primary"
                          onClick={handleModifyProject}
                        >
                          Modify Project
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form>
              </Card>
            </TabPane>

            <TabPane tab="Activate/Inactivate" key="1.3">
              <Card className="form-card" bordered>
                <AdminTable
                  fetchUrl={`${PY_API_URL}/api/get_projects_admin`}
                  updateUrl={`${PY_API_URL}/api/update_project_status`}
                  columns={ProjectColumns}
                  applyShowFilter={true}
                  primaryKey="projectNumber"
                />
              </Card>
            </TabPane>
          </Tabs>
        </TabPane>
        <TabPane tab="Job Type" key="2">
          <Tabs centered>
            <TabPane tab="Add" key="2.1">
              <Card className="form-card" bordered>
                <Form
                  layout="horizontal"
                  labelCol={{ span: 6 }}
                  wrapperCol={{ span: 16 }}
                  style={{ maxWidth: 600 }}
                >
                  <Form.Item label="Test Type">
                    <Input
                      value={testType}
                      onChange={handleTestTypeChange}
                      placeholder="ETO"
                    />
                  </Form.Item>
                  <Form.Item label="Group">
                    <Input
                      value={group}
                      onChange={handleGroupChange}
                      placeholder="OH"
                    />
                  </Form.Item>
                  <Form.Item className="form-item">
                    <Button
                      className="form-btn"
                      type="primary"
                      onClick={handleSubmitJobType}
                    >
                      Submit New Job Type
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            </TabPane>

            <TabPane tab="Modify" key="2.2">
              <Card className="form-card" bordered>
                <Form
                  layout="horizontal"
                  labelCol={{ span: 6 }}
                  wrapperCol={{ span: 16 }}
                  style={{ maxWidth: 600 }}
                >
                  <Form.Item label="Select Job Type">
                    <Select
                      showSearch
                      placeholder="Select a job type to modify"
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option?.children
                          ?.toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      onChange={(value) => {
                        setSelectedJobType(value);
                        const selected = jobTypes[value];

                        if (selected) {
                          setTestType(value);
                          setGroup(selected.group || "");
                        }
                      }}
                    >
                      {Object.keys(jobTypes).map((jobType) => (
                        <Option key={jobType} value={jobType}>
                          {jobType}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  {selectedJobType && (
                    <>
                      <Form.Item label="Test Type">
                        <Input
                          value={testType}
                          onChange={(e) => setTestType(e.target.value)}
                        />
                      </Form.Item>
                      <Form.Item label="Group">
                        <Input
                          value={group}
                          onChange={(e) => setGroup(e.target.value)}
                        />
                      </Form.Item>
                      <Form.Item className="form-item">
                        <Button
                          className="form-btn"
                          type="primary"
                          onClick={handleModifyJobType}
                        >
                          Modify Job Type
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form>
              </Card>
            </TabPane>

            <TabPane tab="Activate/Inactivate" key="2.3">
              <Card className="form-card" bordered>
                <AdminTable
                  fetchUrl={`${PY_API_URL}/api/get_job_types_admin`}
                  updateUrl={`${PY_API_URL}/api/update_job_type_status`}
                  columns={JobTypeColumns}
                  primaryKey="test_type"
                />
              </Card>
            </TabPane>
          </Tabs>
        </TabPane>
        <TabPane tab="Cost Center Details" key="3">
          <Tabs centered>
            <TabPane tab="Add" key="3.1">
              <Card className="form-card" bordered>
                <Form
                  layout="horizontal"
                  labelCol={{ span: 6 }}
                  wrapperCol={{ span: 16 }}
                  style={{ maxWidth: 600 }}
                >
                  <Form.Item label="Project Number">
                    <Input
                      value={costCenterProjectNumber}
                      onChange={handleCostCenterProjectNumberChange}
                      placeholder="904931"
                    />
                  </Form.Item>
                  <Form.Item label="Program">
                    <Input
                      value={costCenterProgram}
                      onChange={handleCostCenterProgramChange}
                      placeholder="OFerrari Serratura DX CPLH"
                    />
                  </Form.Item>
                  <Form.Item label="PM">
                    <Input
                      value={costCenterPm}
                      onChange={handleCostCenterPmChange}
                      placeholder="Carmelo Gioffre"
                    />
                  </Form.Item>
                  <Form.Item label="BCKPM">
                    <Input
                      value={bckPm}
                      onChange={handleBckPmChange}
                      placeholder="Carmelo Gioffre"
                    />
                  </Form.Item>
                  <Form.Item label="CDC">
                    <Input
                      value={costCenterCdc}
                      onChange={handleCostCenterCdcChange}
                      placeholder="OH"
                    />
                  </Form.Item>
                  <Form.Item label="Charge To">
                    <Input
                      value={chargeTo}
                      onChange={handleChargeToChange}
                      placeholder="Motrol (288)"
                    />
                  </Form.Item>
                  <Form.Item className="form-item">
                    <Button
                      className="form-btn"
                      type="primary"
                      onClick={handleSubmitCostCenter}
                      disabled={
                        !costCenterProjectNumber.trim() ||
                        !costCenterProgram.trim() ||
                        !costCenterPm.trim() ||
                        !bckPm.trim() ||
                        !chargeTo.trim()
                      }
                    >
                      Submit New Cost Center Details
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            </TabPane>
            <TabPane tab="Modify" key="3.2">
              <Card className="form-card" bordered>
                <Form
                  layout="horizontal"
                  labelCol={{ span: 6 }}
                  wrapperCol={{ span: 16 }}
                  style={{ maxWidth: 600 }}
                >
                  <Form.Item label="Select Cost Center">
                    <Select
                      showSearch
                      placeholder="Select a cost center to modify"
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option?.children
                          ?.toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      onChange={(value) => {
                        setSelectedCostCenter(value);
                        const selected = costCenters.find(
                          (center) => center.costCenter === value
                        );
                        if (selected) {
                          setCostCenterProjectNumber(selected.costCenter);
                          setCostCenterProgram(
                            selected.costCenterDescription || ""
                          );
                          setCostCenterPm(selected.costCenterManager || "");
                          setBckPm(selected.costCenterApprover || "");
                          setCostCenterCdc(selected.cdc || "");
                          setChargeTo(selected.chargeTo || "");
                        }
                      }}
                    >
                      {costCenters.map((center) => (
                        <Option
                          key={center.costCenter+center.costCenterDescription}
                          value={center.costCenter}
                        >
                          {`${center.costCenter} - ${center.costCenterDescription}`}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  {selectedCostCenter && (
                    <>
                      <Form.Item label="Program">
                        <Input
                          value={costCenterProgram}
                          onChange={(e) => setCostCenterProgram(e.target.value)}
                        />
                      </Form.Item>
                      <Form.Item label="PM">
                        <Input
                          value={costCenterPm}
                          onChange={(e) => setCostCenterPm(e.target.value)}
                        />
                      </Form.Item>
                      <Form.Item label="BCKPM">
                        <Input
                          value={bckPm}
                          onChange={(e) => setBckPm(e.target.value)}
                        />
                      </Form.Item>
                      <Form.Item label="CDC">
                        <Input
                          value={costCenterCdc}
                          onChange={(e) => setCostCenterCdc(e.target.value)}
                        />
                      </Form.Item>
                      <Form.Item label="Charge To">
                        <Input
                          value={chargeTo}
                          onChange={(e) => setChargeTo(e.target.value)}
                        />
                      </Form.Item>

                      <Form.Item className="form-item">
                        <Button
                          className="form-btn"
                          type="primary"
                          onClick={handleModifyCostCenter}
                        >
                          Modify Cost Center
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form>
              </Card>
            </TabPane>

            <TabPane tab="Activate/Inactivate" key="3.3">
              <Card className="form-card" bordered>
                <AdminTable
                  fetchUrl={`${PY_API_URL}/api/get_cost_centers_admin`}
                  updateUrl={`${PY_API_URL}/api/update_cost_center_status`}
                  columns={CostCenterColumns}
                  applyShowFilter={false}
                  primaryKey="costCenter"
                />
              </Card>
            </TabPane>
          </Tabs>
        </TabPane>

        <TabPane tab="Fixture" key="4">
          <Tabs centered>
            <TabPane tab="Add" key="4.1">
              <Card className="form-card" bordered>
                <Form
                  layout="horizontal"
                  labelCol={{ span: 6 }}
                  wrapperCol={{ span: 16 }}
                  style={{ maxWidth: 600 }}
                >
                  <Form.Item label="Fixture Name">
                    <Input
                      value={fixtureName}
                      onChange={handleFixtureNameChange}
                      placeholder="Galdabini Quasar 100"
                    />
                  </Form.Item>
                  <Form.Item className="form-item">
                    <Button
                      className="form-btn"
                      type="primary"
                      onClick={handleSubmitFixture}
                    >
                      Submit New Fixture
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            </TabPane>

            <TabPane tab="Modify" key="4.2">
              <Card className="form-card" bordered>
                <Form
                  layout="horizontal"
                  labelCol={{ span: 6 }}
                  wrapperCol={{ span: 16 }}
                  style={{ maxWidth: 600 }}
                >
                  <Form.Item label="Select Fixture">
                    <Select
                      showSearch
                      placeholder="Select a fixture to modify"
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option?.children
                          ?.toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      onChange={(value) => setSelectedFixture(value)}
                    >
                      {fixtures.map((fixture, index) => (
                        <Option key={index} value={fixture.name}>
                          {fixture.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  {selectedFixture && (
                    <>
                      <Form.Item label="New Fixture Name">
                        <Input
                          placeholder="Enter new fixture name"
                          value={modifiedFixtureName}
                          onChange={(e) =>
                            setModifiedFixtureName(e.target.value)
                          }
                        />
                      </Form.Item>
                      <Form.Item className="form-item">
                        <Button
                          className="form-btn"
                          type="primary"
                          onClick={handleModifyFixture}
                          disabled={!selectedFixture || !modifiedFixtureName}
                        >
                          Modify Fixture
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form>
              </Card>
            </TabPane>

            <TabPane tab="Activate/Inactivate" key="4.3">
              <Card className="form-card" bordered>
                <AdminTable
                  fetchUrl={`${PY_API_URL}/api/get_fixtures_admin`}
                  updateUrl={`${PY_API_URL}/api/update_fixture_status`}
                  columns={FixtureColumns}
                  primaryKey="name"
                />
              </Card>
            </TabPane>
          </Tabs>
        </TabPane>
        <TabPane tab="Climatic Chamber " key="5">
          <Tabs centered>
            <TabPane tab="Add " key="5.1">
              <Card className="form-card" bordered>
                <Form
                  layout="horizontal"
                  labelCol={{ span: 7 }}
                  wrapperCol={{ span: 16 }}
                  style={{ maxWidth: 700 }}
                >
                  <Form.Item label="Climatic Chamber Name">
                    <Input
                      value={climaticChamberName}
                      onChange={handleClimaticChamberNameChange}
                      placeholder="Camera 01 ACS"
                    />
                  </Form.Item>
                  <Form.Item className="form-item">
                    <Button
                      className="form-btn"
                      type="primary"
                      onClick={handleSubmitClimaticChamber}
                    >
                      Submit New Climatic Chamber
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            </TabPane>
            <TabPane tab="Modify" key="5.2">
              <Card className="form-card" bordered>
                <Form
                  layout="horizontal"
                  labelCol={{ span: 7 }}
                  wrapperCol={{ span: 18 }}
                  style={{ maxWidth: 800 }}
                >
                  <Form.Item label="Select Climatic Chamber">
                    <Select
                      showSearch
                      placeholder="Select a climatic chamber to modify"
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option?.children
                          ?.toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      onChange={(value) => setSelectedChamber(value)}
                    >
                      {climaticChambers.map((chamber, index) => (
                        <Option key={index} value={chamber.name}>
                          {chamber.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  {selectedChamber && (
                    <>
                      <Form.Item label="New Climatic Chamber Name">
                        <Input
                          placeholder="Enter new climatic chamber name"
                          value={modifiedChamberName}
                          onChange={(e) =>
                            setModifiedChamberName(e.target.value)
                          }
                        />
                      </Form.Item>
                      <Form.Item className="form-item">
                        <Button
                          className="form-btn"
                          type="primary"
                          onClick={handleModifyChamber}
                          disabled={!selectedChamber || !modifiedChamberName}
                        >
                          Modify Climatic Chamber
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form>
              </Card>
            </TabPane>

            <TabPane tab="Activate/Inactivate" key="5.3">
              <Card className="form-card" bordered>
                <AdminTable
                  fetchUrl={`${PY_API_URL}/api/get_climatic_chambers_admin`}
                  updateUrl={`${PY_API_URL}/api/update_climatic_chamber_status`}
                  columns={ClimaticChamberColumns}
                  primaryKey="name"
                />
              </Card>
            </TabPane>
          </Tabs>
        </TabPane>
        <TabPane tab="Test Instruction" key="6">
          <Tabs centered>
            {/* <TabPane tab="Track Test Instructions" key="6.1">
              <Tabs
                centered
                tabBarStyle={{
                  textAlign: "center",
                  fontSize: "14px",
                  marginBottom: "10px",
                }}
              > */}
            <TabPane tab="Track Current Test Instructions" key="current">
              <Card className="form-card" bordered>
                <h3 style={{ textAlign: "center", fontSize: "16px" }}>
                  Synchronize Released Test Instructions with Network Folder 📂
                </h3>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: "10px",
                  }}
                >
                  <Button
                    type="primary"
                    onClick={fetchTestInstructionsComparison}
                    loading={loading}
                    icon={<ReloadOutlined />}
                  >
                    Refresh Data
                  </Button>
                  <Button
                    type="primary"
                    color="green"
                    variant="solid"
                    onClick={showSyncConfirm}
                    loading={syncLoading}
                    disabled={loading || comparisonData.length === 0}
                    style={{ marginLeft: "10px" }}
                    icon={<CloudSyncOutlined />}
                  >
                    Synchronize All
                  </Button>
                </div>

                <Modal
                  title="Confirm Synchronization"
                  visible={isModalVisible}
                  onOk={handleSyncConfirm}
                  onCancel={handleSyncCancel}
                  confirmLoading={syncLoading}
                  okText="Yes, Synchronize"
                  cancelText="Cancel"
                >
                  <p>
                    Are you sure you want to synchronize the test instructions?
                  </p>
                  <p>This action will:</p>
                  <ul>
                    <li>Add missing test instructions to the database.</li>
                    <li>
                      Move outdated test instructions to the archive folder.
                    </li>
                  </ul>
                </Modal>
                <Table
                  style={{ width: 600 }}
                  columns={TIcolumns}
                  dataSource={comparisonData}
                  rowKey="testNumber"
                  loading={loading}
                />
              </Card>
            </TabPane>

            <TabPane tab="Track Old Test Instructions" key="old">
              <Card className="form-card" bordered>
                <h3 style={{ textAlign: "center", fontSize: "16px" }}>
                  Synchronize Old Test Instructions with Network Folder 📂
                </h3>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: "10px",
                  }}
                >
                  <Button
                    type="primary"
                    onClick={fetchOldTestInstructionsComparison}
                    loading={oldLoading}
                    icon={<ReloadOutlined />}
                  >
                    Refresh Data
                  </Button>
                  <Button
                    type="primary"
                    color="green"
                    variant="solid"
                    onClick={showOldSyncConfirm}
                    loading={oldSyncLoading}
                    disabled={oldLoading || oldComparisonData.length === 0}
                    style={{ marginLeft: "10px" }}
                    icon={<CloudSyncOutlined />}
                  >
                    Synchronize All
                  </Button>
                </div>

                <Modal
                  title="Confirm Synchronization of Old Test Instructions"
                  visible={isOldModalVisible}
                  onOk={handleOldSyncConfirm}
                  onCancel={handleOldSyncCancel}
                  confirmLoading={oldSyncLoading}
                  okText="Yes, Synchronize"
                  cancelText="Cancel"
                >
                  <p>
                    Are you sure you want to synchronize the old test
                    instructions?
                  </p>
                  <p>This action will:</p>
                  <ul>
                    <li>Add missing old test instructions to the database.</li>
                    <li>
                      Note: It will not move or archive existing test
                      instructions.
                    </li>
                  </ul>
                </Modal>

                <Table
                  style={{ width: 600 }}
                  columns={TIcolumns} // Reuse same columns, or define a new one if different
                  dataSource={oldComparisonData}
                  rowKey="testNumber"
                  loading={oldLoading}
                />
              </Card>
            </TabPane>
            {/* </Tabs>
            </TabPane> */}

            <TabPane tab="Add Test Instruction" key="6.2">
              <Card className="form-card" bordered>
                <Form
                  layout="horizontal"
                  labelCol={{ span: 6 }}
                  wrapperCol={{ span: 16 }}
                  style={{ maxWidth: 700 }}
                >
                  <Form.Item label="Test Name">
                    <Input
                      value={testName}
                      onChange={handleTestNameChange}
                      placeholder="Conditioning"
                    />
                  </Form.Item>
                  <Form.Item
                    label="Test Number"
                    className="form-item-container"
                  >
                    <div className="input-container">
                      <Input
                        value={testNumber}
                        onChange={handleTestNumberChange}
                        maxLength={10}
                        placeholder="T00001-A00"
                      />
                    </div>
                    <div className="tag-container">
                      <Tag icon={<ExclamationCircleOutlined />} color="warning">
                        Make sure the test number matches the uploaded PDF file.
                        Can only be 9 characters long
                      </Tag>
                    </div>
                  </Form.Item>

                  <Form.Item className="form-item">
                    <Upload
                      beforeUpload={handleUpload}
                      accept=".pdf"
                      maxCount={1}
                    >
                      <Button className="form-btn" icon={<UploadOutlined />}>
                        Upload PDF
                      </Button>
                    </Upload>
                  </Form.Item>
                  <Form.Item className="form-item">
                    <Button
                      className="form-btn"
                      type="primary"
                      onClick={handleSubmitTestInstruction}
                    >
                      Submit New Test Instruction
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            </TabPane>
          </Tabs>
        </TabPane>
      </Tabs>
    </Card>
  );
};

export default AdminPanel;
